function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  // Skip menu-drawer and header-drawer - they have their own handlers
  if (summary.closest('header-drawer, menu-drawer')) return;

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;
    
    // Get the button element, not the child SVG
    const button = event.currentTarget || event.target.closest('button');
    const buttonName = button?.getAttribute('name');

    if (buttonName === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && buttonName === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

/** Drawer / overlay height; visualViewport matches on-screen area better than innerHeight on mobile. */
function shopifyViewportHeight() {
  const vv = window.visualViewport;
  return vv && typeof vv.height === 'number' ? Math.round(vv.height) : window.innerHeight;
}

/** getBoundingClientRect Y uses the layout viewport; use this for clamps, not visualViewport.height. */
function shopifyLayoutViewportHeight() {
  return window.innerHeight || document.documentElement.clientHeight || shopifyViewportHeight();
}

class MenuDrawer extends HTMLElement {
  constructor() {
    super();
    this._menuOpeningTimer = null;

    this.mainDetailsToggle = this.querySelector('details');

    // Only bind events if mainDetailsToggle exists
    if (this.mainDetailsToggle) {
      this.addEventListener('keyup', this.onKeyUp.bind(this));
      this.addEventListener('focusout', this.onFocusOut.bind(this));
      this.bindEvents();
    } else {
      console.warn('MenuDrawer: details element not found, menu drawer will not function');
    }
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
    
    // Prevent drawer from closing when clicking on filter inputs
    if (this.classList.contains('mobile-facets__wrapper')) {
      this.addEventListener('focusout', (event) => {
        // If clicking on a filter input, prevent the drawer from closing
        const target = event.target;
        const relatedTarget = event.relatedTarget;
        
        if (target && (
          target.closest('.mobile-facets__checkbox') ||
          target.closest('input[type="checkbox"]') ||
          target.closest('input[type="text"]') ||
          target.closest('.js-filter') ||
          target.closest('.mobile-facets__list')
        )) {
          event.stopPropagation();
        }
      }, true); // Use capture phase to catch the event early

      // Click the dimmed form shell (outside .mobile-facets__inner) to close.
      // onFocusOut is intentionally skipped for facets, so this is the outside-dismiss path.
      const facetForm = this.querySelector('form.mobile-facets');
      if (facetForm) {
        facetForm.addEventListener('click', (event) => {
          if (event.target !== facetForm) return;
          if (!this.mainDetailsToggle?.hasAttribute('open')) return;
          this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'));
        });
      }
    }
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;
    if (!this.mainDetailsToggle) return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    if (detailsElement === this.mainDetailsToggle) {
      event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${shopifyViewportHeight()}px`);
      }
    } else {
      // Prevent default behavior for submenu items - let JS handle it
      event.preventDefault();

      // Toggle the submenu manually
      if (isOpen) {
        this.closeSubmenu(detailsElement);
        document.body.classList.remove('submenu-open');
      } else {
        detailsElement.setAttribute('open', '');
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        document.body.classList.add('submenu-open');
      }
    }
  }

  openMenuDrawer(summaryElement) {
    if (!this.mainDetailsToggle) return;

    if (this._menuOpeningTimer) {
      clearTimeout(this._menuOpeningTimer);
      this._menuOpeningTimer = null;
    }
    if (this._menuClosingTimer) {
      clearTimeout(this._menuClosingTimer);
      this._menuClosingTimer = null;
    }

    this.mainDetailsToggle.setAttribute('open', '');
    this._menuOpeningTimer = setTimeout(() => {
      this._menuOpeningTimer = null;
      if (!this.mainDetailsToggle || !this.mainDetailsToggle.hasAttribute('open')) return;
      this.mainDetailsToggle.classList.add('menu-opening');
    }, 0);
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;
    if (!this.mainDetailsToggle) return;

    if (this._menuOpeningTimer) {
      clearTimeout(this._menuOpeningTimer);
      this._menuOpeningTimer = null;
    }

    const isMobileFacets = this.classList.contains('mobile-facets__wrapper');
    const summaryElement =
      elementToFocus || this.mainDetailsToggle.querySelector('summary');

    // Start reverse slide: keep [open] so details content stays visible during transition
    this.mainDetailsToggle.classList.remove('menu-opening');

    // Close nested filter submenus immediately
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });

    document.body.classList.remove('submenu-open');
    summaryElement?.setAttribute('aria-expanded', false);

    if (isMobileFacets) {
      if (this._menuClosingTimer) {
        clearTimeout(this._menuClosingTimer);
        this._menuClosingTimer = null;
      }

      const inner = this.querySelector('.mobile-facets__inner');
      const durationMs = this.getTransitionDurationMs(inner) || 200;

      this._menuClosingTimer = setTimeout(() => {
        this._menuClosingTimer = null;
        if (!this.mainDetailsToggle) return;
        document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
        removeTrapFocus(summaryElement);
        this.mainDetailsToggle.removeAttribute('open');
      }, durationMs);
      return;
    }

    // Non-facets drawers: close immediately
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.mainDetailsToggle.removeAttribute('open');
    void this.mainDetailsToggle.offsetHeight;

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  getTransitionDurationMs(element) {
    if (!element) return 0;
    const raw = getComputedStyle(element).transitionDuration.split(',')[0].trim();
    if (!raw) return 0;
    if (raw.endsWith('ms')) return parseFloat(raw) || 0;
    if (raw.endsWith('s')) return (parseFloat(raw) || 0) * 1000;
    return 0;
  }

  onFocusOut(event) {
    if (!this.mainDetailsToggle) return;
    
    // For mobile facets drawer, don't close on focusout - let user explicitly close it or close after filter applied
    const isMobileFacets = this.classList.contains('mobile-facets__wrapper');
    const isMobile = window.matchMedia('(max-width: 989px)').matches;
    
    if (isMobileFacets) {
      return; // Don't close facets drawer on focusout (any breakpoint)
    }
    
    // Don't close if a submenu is open (check both in-drawer and repositioned to body)
    const hasOpenSubmenu = this.querySelector('details[open] .menu-drawer__submenu') || 
                           document.querySelector('.menu-drawer__submenu[data-repositioned="true"]') ||
                           document.body.classList.contains('submenu-open');
    if (hasOpenSubmenu) {
      return;
    }
    
    // For desktop drawers, close on focusout (click outside)
    setTimeout(() => {
      // Only close if focus has completely left the menu drawer
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement)) {
        // Check if the new focus target is still within the menu
        const relatedTarget = event.relatedTarget;
        
        if (!relatedTarget || !this.contains(relatedTarget)) {
          this.closeMenuDrawer(event);
        }
      }
    }, 50);
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    detailsElement.classList.remove('menu-opening');
    detailsElement.classList.add('menu-closing');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    document.body.classList.remove('submenu-open');
    
    // Wait for animation to complete before removing open attribute
    setTimeout(() => {
      detailsElement.removeAttribute('open');
      detailsElement.classList.remove('menu-closing');
    }, 400);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();

    if (!this.mainDetailsToggle) {
      return;
    }
  }

  /**
   * Sync --header-group-sticky-top (announcement layout bottom) and --header-bottom-position
   * after header / announcement paint. Uses layout viewport height for clamps (not visualViewport).
   */
  syncHeaderLayoutMetrics(menuDrawerContainer) {
    const isOpen = this.mainDetailsToggle?.hasAttribute('open');

    /* Fix both the announcement bar and section-header to position:fixed so that scroll-lock
       (html { overflow:hidden }) cannot break sticky positioning, and transition:none ensures
       getBoundingClientRect() reflects the final layout position, not a mid-animation value. */
    const ann = document.querySelector('.announcement-bar-section');
    if (ann && isOpen) {
      ann.style.setProperty('transition', 'none', 'important');
      ann.style.setProperty('position', 'fixed', 'important');
      ann.style.setProperty('top', '0', 'important');
      ann.style.setProperty('left', '0', 'important');
      ann.style.setProperty('right', '0', 'important');
      ann.style.setProperty('z-index', '10002', 'important');
    }

    // Force reflow so the announcement bar's fixed position is painted before we measure.
    if (ann && isOpen) void ann.offsetHeight;

    let stickyTopPx = 0;
    if (ann) {
      if (isOpen) {
        // Now fixed at top:0 — its bottom equals its rendered height, always accurate.
        stickyTopPx = Math.max(0, Math.round(ann.getBoundingClientRect().bottom));
      } else {
        const ar = ann.getBoundingClientRect();
        if (ar.height > 0 && ar.bottom > 0) stickyTopPx = Math.max(0, Math.round(ar.bottom));
      }
      document.documentElement.style.setProperty('--header-group-sticky-top', `${stickyTopPx}px`);
    } else {
      document.documentElement.style.removeProperty('--header-group-sticky-top');
    }

    const section = this.header || document.querySelector('.section-header');
    if (section && isOpen) {
      section.style.setProperty('transition', 'none', 'important');
      section.style.setProperty('position', 'fixed', 'important');
      section.style.setProperty('top', `${stickyTopPx}px`, 'important');
      section.style.setProperty('left', '0', 'important');
      section.style.setProperty('right', '0', 'important');
      section.style.setProperty('z-index', '10001', 'important');
    }

    /* Measure the .header-wrapper (sticky-header element) — the visible bar only, excluding any
       sibling elements (localization drawer, notification markup) that live inside .section-header
       but below the bar. Using .section-header when it is fixed would include those hidden-flow
       elements and set --header-bottom-position too large, creating a gap. */
    const headerWrapperEl = (section && section.querySelector('.header-wrapper')) ||
                             this.closest('.header-wrapper') ||
                             this.closest('.header') ||
                             this.header;
    if (headerWrapperEl) {
      const layoutH = shopifyLayoutViewportHeight();
      let bottomPx = Math.round(headerWrapperEl.getBoundingClientRect().bottom - this.borderOffset);
      bottomPx = Math.max(0, Math.min(bottomPx, layoutH));
      document.documentElement.style.setProperty('--header-bottom-position', `${bottomPx}px`);
    }

    document.documentElement.style.setProperty('--viewport-height', `${shopifyViewportHeight()}px`);

    if (menuDrawerContainer) {
      const containerRect = menuDrawerContainer.getBoundingClientRect();
      const leftOffset = containerRect.left;
      const menuDrawer = menuDrawerContainer.querySelector('.menu-drawer');
      if (menuDrawer) {
        menuDrawer.style.left = `${-leftOffset}px`;
      }
    }
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;

    const menuDrawerContainer = this.mainDetailsToggle || this.querySelector('.menu-drawer-container');

    // Open <details> first so drawer CSS (details[open]) applies before header stacking tweaks.
    this.mainDetailsToggle.setAttribute('open', '');

    // Force the section-header visible before scroll-lock and measurements.
    // Also remove `animate` to prevent the CSS `transition: top 0.15s` from firing when we later
    // set position:fixed inline — a mid-transition getBoundingClientRect() would give a wrong
    // bottom value and position the drawer too low (the visible gap).
    if (this.header) {
      this.header.classList.remove('shopify-section-header-hidden', 'animate');
      this.header.classList.add('shopify-section-header-sticky', 'menu-open');
    }

    // Also unhide the announcement bar so it is visible above the header while the menu is open.
    const ann = document.querySelector('.announcement-bar-section');
    if (ann) ann.classList.remove('shopify-section-header-hidden', 'animate');

    // Force reflow so class changes are computed before we measure geometry.
    if (this.header) void this.header.offsetHeight;

    this.syncHeaderLayoutMetrics(menuDrawerContainer);

    if (this._menuOpeningTimer) {
      clearTimeout(this._menuOpeningTimer);
      this._menuOpeningTimer = null;
    }
    this._menuOpeningTimer = setTimeout(() => {
      this._menuOpeningTimer = null;
      if (!this.mainDetailsToggle || !this.mainDetailsToggle.hasAttribute('open')) return;
      this.mainDetailsToggle.classList.add('menu-opening');

      requestAnimationFrame(() => {
        if (!this.mainDetailsToggle || !this.mainDetailsToggle.hasAttribute('open')) return;
        this.syncHeaderLayoutMetrics(menuDrawerContainer);
      });
    }, 0);

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    // Fix: Don't return early - allow closing even without elementToFocus
    if (event === undefined) return;

    if (this._menuOpeningTimer) {
      clearTimeout(this._menuOpeningTimer);
      this._menuOpeningTimer = null;
    }

    // Ensure mainDetailsToggle exists
    if (!this.mainDetailsToggle) {
      this.mainDetailsToggle = this.querySelector('details');
      if (!this.mainDetailsToggle) {
        console.error('HeaderDrawer: Cannot close - details element not found');
        return;
      }
    }
    
    try {
      super.closeMenuDrawer(event, elementToFocus);
    } finally {
      const hdr = document.querySelector('.section-header');
      if (hdr) {
        hdr.classList.remove('menu-open');
        hdr.style.removeProperty('transition');
        hdr.style.removeProperty('position');
        hdr.style.removeProperty('top');
        hdr.style.removeProperty('left');
        hdr.style.removeProperty('right');
        hdr.style.removeProperty('z-index');
      }
      // Restore announcement bar inline overrides so StickyHeader resumes normal control.
      const ann = document.querySelector('.announcement-bar-section');
      if (ann) {
        ann.style.removeProperty('transition');
        ann.style.removeProperty('position');
        ann.style.removeProperty('top');
        ann.style.removeProperty('left');
        ann.style.removeProperty('right');
        ann.style.removeProperty('z-index');
      }
      this.mainDetailsToggle?.classList.remove('menu-opening');
      document.documentElement.style.removeProperty('--header-group-sticky-top');
    }
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    if (!this.header) return;
    if (!this.mainDetailsToggle?.hasAttribute('open')) return;

    const menuDrawerContainer = this.mainDetailsToggle || this.querySelector('.menu-drawer-container');
    this.syncHeaderLayoutMetrics(menuDrawerContainer);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch(() => {
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      // Disable modal opening on mobile for product modals
      const isMobile = window.matchMedia('(max-width: 989px)').matches;
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (isMobile && modal && modal.classList.contains('product-media-modal')) {
        return;
      }
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.sliderItemOffset) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (!this.nextButton) return;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) {
      const shouldAutoplay =
        this.slider && this.slider.getAttribute('data-autoplay') === 'true' && this.sliderItems.length > 1;
      if (shouldAutoplay) {
        this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
        this.announcerBarAnimationDelay = 0;
        this.initPages();
        const resizeObserver = new ResizeObserver(() => this.initPages());
        resizeObserver.observe(this.slider);
        this.slider.addEventListener('scroll', this.update.bind(this));
        this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
        this.setSlideVisibility();
        this.setAutoPlay();
      }
      return;
    }

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    if (!this.reducedMotion) {
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    }
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    if (!this.prevButton) return;
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider && this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const maxScroll = this.slider.scrollWidth - this.slider.clientWidth;
    const atEnd = this.currentPage === this.sliderItems.length || this.slider.scrollLeft >= maxScroll - 1;
    const slideScrollPosition = atEnd ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

// Complementary Products - Load recommendations as a block within main product section
(() => {
  const el = document.querySelector('[data-recommendations-block][data-intent="complementary"]');
  if (!el || !window.Shopify || !Shopify.routes) return;

  const productId = el.dataset.productId;
  const sectionId = el.dataset.sectionId; // main section id (template--...__main)
  const limit = Number(el.dataset.limit || 4);

  const loadRecommendations = (intent = 'complementary') => {
    const url =
      `${Shopify.routes.root}recommendations/products` +
      `?section_id=${encodeURIComponent(sectionId)}` +
      `&product_id=${encodeURIComponent(productId)}` +
      `&intent=${intent}&limit=${limit}&_=${Date.now()}`;

    fetch(url)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then(html => {
        
        const tpl = document.createElement('template');
        tpl.innerHTML = html.trim();

        // Our Liquid responder returns a subtree with data-rec-root inside the main section
        const incoming = tpl.content.querySelector(`#shopify-section-${CSS.escape(sectionId)} [data-rec-root]`);
        if (!incoming) {
          // Try fallback to related if this was complementary
          if (intent === 'complementary') {
            loadRecommendations('related');
          } else {
            el.querySelector('[data-placeholder]')?.replaceWith(document.createComment('no recommendations'));
          }
          return;
        }

        // Check for empty state
        const emptyState = incoming.querySelector('[data-empty-state]');
        if (emptyState) {
          // Try fallback to related if this was complementary
          if (intent === 'complementary') {
            loadRecommendations('related');
          } else {
            // Hide the entire block if no recommendations found
            const blockWrapper = el.closest('.product-complete-look-block');
            if (blockWrapper) {
              blockWrapper.style.display = 'none';
            }
            el.querySelector('[data-placeholder]')?.replaceWith(document.createComment('no recommendations'));
          }
          return;
        }

        const cards = incoming.querySelectorAll('[data-product-card], .card, .grid__item');
        
        if (cards.length === 0) {
          // Try fallback to related if this was complementary
          if (intent === 'complementary') {
            loadRecommendations('related');
          } else {
            el.querySelector('[data-placeholder]')?.replaceWith(document.createComment('no complementary products'));
          }
          return;
        }

        el.innerHTML = incoming.innerHTML; // inject cards into the block container
        
        // Setup add to cart buttons
        setupCompleteTheLookButtons();
      })
      .catch(() => {
        const placeholder = el.querySelector('[data-placeholder]');
        if (placeholder) {
          placeholder.innerHTML = '<div style="text-align: center; padding: 20px; color: #999; font-size: 14px;">Unable to load recommendations</div>';
        }
      });
  };
  
  // Setup add to cart functionality
  function setupCompleteTheLookButtons() {
    const buttons = document.querySelectorAll('.complete-look-add-btn');
    
    buttons.forEach(button => {
      button.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        
        if (button.disabled || button.classList.contains('loading')) return;
        
        const variantId = button.dataset.variantId;
        const originalText = button.textContent;
        
        button.classList.add('loading');
        button.textContent = 'Adding...';
        
        // Add to cart
        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: [{
              id: variantId,
              quantity: 1
            }]
          })
        })
        .then(response => response.json())
        .then(data => {
          // Show success feedback
          button.textContent = 'Added ✓';
          
          // Update cart count if cart drawer exists
          if (window.theme && window.theme.updateCart) {
            window.theme.updateCart();
          }
          
          // Trigger cart drawer to open if it exists
          const cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && cartDrawer.open) {
            cartDrawer.open();
          }
          
          // Reset button after 2 seconds
          setTimeout(() => {
            button.textContent = originalText;
            button.classList.remove('loading');
          }, 2000);
        })
        .catch(() => {
          button.textContent = 'Error';
          button.classList.remove('loading');
          
          setTimeout(() => {
            button.textContent = originalText;
          }, 2000);
        });
      });
    });
  }

  // Load immediately in theme editor, or use IntersectionObserver for live site
  const isShopifyEditor = window.Shopify?.designMode || 
                         document.body?.classList.contains('shopify-design-mode') ||
                         window.location.search.includes('preview_theme_id');

  if (isShopifyEditor) {
    setTimeout(() => loadRecommendations(), 100);
  } else {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        observer.disconnect();
        loadRecommendations();
      }
    }, { rootMargin: '0px 0px 400px 0px' });
    observer.observe(el);
  }
})();

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

/**
 * Sibling Swatch Handler
 * Swaps card images and links on hover/focus of color swatches
 * Progressive enhancement: works as normal links if JS disabled
 */
document.addEventListener('mouseover', onSwatchEvent, { passive: true });
document.addEventListener('focusin', onSwatchEvent);
document.addEventListener('mouseout', onLeaveGroup, { passive: true });
document.addEventListener('focusout', onLeaveGroup);

function onSwatchEvent(e) {
  const swatch = e.target.closest?.('[data-sibling-swatch]');
  if (!swatch) return;

  const group = swatch.closest('[data-sibling-swatch-group]');
  const card  = swatch.closest('[data-product-card]');
  if (!group || !card) return;

  const imgEl  = card.querySelector('[data-card-image]');
  const linkEl = card.querySelector('[data-card-link]');
  if (!imgEl || !linkEl) return;

  const newSrc    = swatch.getAttribute('data-image-src');
  const newSrcset = swatch.getAttribute('data-image-srcset');
  const newHref   = swatch.getAttribute('data-href');

  if (newSrc)    imgEl.src    = newSrc;
  if (newSrcset) imgEl.srcset = newSrcset;
  if (newHref)   linkEl.href  = newHref;
}

function onLeaveGroup(e) {
  const leavingGroup = e.target.closest?.('[data-sibling-swatch-group]');
  if (!leavingGroup) return;

  const related = e.relatedTarget;
  if (related && leavingGroup.contains?.(related)) return; // still inside the swatch group

  const card  = leavingGroup.closest('[data-product-card]');
  const imgEl = card?.querySelector('[data-card-image]');
  const linkEl = card?.querySelector('[data-card-link]');
  if (!imgEl || !linkEl) return;

  imgEl.src    = imgEl.getAttribute('data-default-src');
  imgEl.srcset = imgEl.getAttribute('data-default-srcset');
  linkEl.href  = imgEl.getAttribute('data-default-href');
}

/**
 * Localization Drawer Handler
 * Opens/closes drawer and handles tab switching
 */
document.addEventListener('DOMContentLoaded', function () {
  const drawer = document.getElementById('wt-localization-drawer');
  const triggers = document.querySelectorAll('.wt-localization-trigger');
  const closeBtn = drawer?.querySelector('.wt-localization-drawer__close');

  if (triggers.length > 0 && drawer) {
    triggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        drawer.classList.add('wt-localization-drawer--open');
        drawer.setAttribute('aria-hidden', 'false');
        triggers.forEach(t => t.setAttribute('aria-expanded', 'true'));
        document.body.style.overflow = 'hidden';
      });
    });
  }

  if (closeBtn && drawer) {
    closeBtn.addEventListener('click', () => {
      drawer.classList.remove('wt-localization-drawer--open');
      drawer.setAttribute('aria-hidden', 'true');
      triggers.forEach(t => t.setAttribute('aria-expanded', 'false'));
      document.body.style.overflow = '';
    });
  }

  // Close when clicking backdrop
  drawer?.addEventListener('click', (e) => {
    if (e.target === drawer) {
      drawer.classList.remove('wt-localization-drawer--open');
      drawer.setAttribute('aria-hidden', 'true');
      triggers.forEach(t => t.setAttribute('aria-expanded', 'false'));
      document.body.style.overflow = '';
    }
  });

  // Close on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer?.classList.contains('wt-localization-drawer--open')) {
      drawer.classList.remove('wt-localization-drawer--open');
      drawer.setAttribute('aria-hidden', 'true');
      triggers.forEach(t => t.setAttribute('aria-expanded', 'false'));
      document.body.style.overflow = '';
    }
  });

  // Tabs
  const tabButtons = drawer?.querySelectorAll('.wt-localization-drawer__tab__trigger');
  const tabContents = drawer?.querySelectorAll('.wt-localization-drawer__tab__content');

  tabButtons?.forEach(btn => {
    btn.addEventListener('click', () => {
      const target = btn.getAttribute('data-tab-target');

      tabButtons.forEach(b => b.classList.remove('wt-localization-drawer__tab__trigger--active'));
      btn.classList.add('wt-localization-drawer__tab__trigger--active');

      tabContents.forEach(content => {
        if (content.getAttribute('data-tab-content') === target) {
          content.classList.add('is-active');
        } else {
          content.classList.remove('is-active');
        }
      });
    });
  });
});

// Card Product Phe Swatch Navigation
(function() {
  'use strict';

  function initCardProductPheSwatches() {
    // Use event delegation to handle dynamically loaded swatches
    document.addEventListener('click', function(e) {
      const swatch = e.target.closest('[data-card-product-phe-swatch="true"]');
      if (!swatch) return;

      // Get the URL from the href attribute
      const swatchUrl = swatch.getAttribute('href');
      if (swatchUrl) {
        window.location.href = swatchUrl;
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCardProductPheSwatches);
  } else {
    initCardProductPheSwatches();
  }

  // Re-initialize when Shopify theme editor loads new sections
  document.addEventListener('shopify:section:load', initCardProductPheSwatches);
})();


